import { useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useViewpointPoiHeight } from '@/components/Analysis/Viewer/hooks/useViewpointPoiHeight';
import { CustomTextField, DropDownField } from '@/components/shared/CustomField';
import { CustomDateField } from '@/components/shared/CustomField/FieldTemplates/CustomDateField';
import {
  PoiStatus,
  PoiAccessibilityEnum,
  VisibilityEnum,
  UnitsEnum,
} from '@/__generated__/graphql';
import { UseGeneralFieldsHookType } from '../hooks';
import * as state from '@/components/Analysis/state';
import { AbyssAutoComplete } from '@/components/shared/AbyssAutoComplete';
import { AutoCompleteOption } from '@/types';
import { formatEnumForDisplay } from '@/utils';
import * as poiState from '../../../state';
import { localiseMeasurement } from '@/utils/unitSystem';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAllWorkpacksQuery } from '@/__generated__/graphql';
import { useGroupPointOfInterestsByWorkpackIdMutation, useRemoveWorkpackIdFromPointOfInterestsMutation } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

type Props = {
  dataHook: UseGeneralFieldsHookType;
  teamsLoading?: boolean;
  teamOptions?: AutoCompleteOption[];
  currentPoiType?: string | undefined;
  paintRegionOptions?: AutoCompleteOption[];
  selectedPaintRegionId?: string;
  handlePaintRegionChange?: (paintRegionName: string) => void;
};

export const GeneralFieldsForm = ({
  dataHook,
  teamsLoading,
  teamOptions,
  currentPoiType = 'POI',
  paintRegionOptions,
  selectedPaintRegionId,
  handlePaintRegionChange,
}: Props) => {
  const {
    state: {
      assignee,
      dueDate,
      status,
      visibility,
      name,
      poiId,
      teamIds,
      comment,
      accessibility,
    },
    updateValue,
  } = dataHook;
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const heightToPointOfInterest = useViewpointPoiHeight();
  const poi = useAtomValue(poiState.editPointOfInterest)?.pointOfInterest;
  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  // Fetch all workpacks for the current structure
  const { data: workpacksData } = useAllWorkpacksQuery({
    variables: { structureId },
    skip: !structureId,
  });

  // Prepare workpack options for the dropdown and create ID mapping
  const { workpackOptions, workpackIdMap } = useMemo(() => {
    const options = workpacksData?.allWorkpacks?.map(workpack => workpack.name) || [];
    const idMap = workpacksData?.allWorkpacks?.reduce((acc, workpack) => {
      acc[workpack.name] = workpack.id;
      return acc;
    }, {} as Record<string, string>) || {};
    
    return {
      workpackOptions: options,
      workpackIdMap: idMap
    };
  }, [workpacksData]);

  // Mutation to assign a workpack to POIs
  const [assignWorkpack] = useGroupPointOfInterestsByWorkpackIdMutation({
    refetchQueries: ['GetPointOfInterest'],
  });

  // Mutation to remove a workpack from POIs
  const [removeWorkpack] = useRemoveWorkpackIdFromPointOfInterestsMutation({
    refetchQueries: ['GetPointOfInterest'],
  });

  // Handle workpack selection change
  const handleWorkpackChange = useCallback(async (value: string) => {
    if (!poi?.id) return;
    
    try {
      const workpackId = value ? workpackIdMap[value] : undefined;
      
      if (workpackId) {
        // Assign workpack
        await assignWorkpack({
          variables: {
            input: {
              workpackId: workpackId,
              pointOfInterestIds: [poi.id],
            },
          },
        });
        showSnackBar('Workpack assigned successfully', 'success');
      } else if (poi.workpack?.id) {
        // Remove workpack if empty value is selected and POI has a workpack
        await removeWorkpack({
          variables: {
            input: {
              workpackId: poi.workpack.id,
              pointOfInterestIds: [poi.id],
            },
          },
        });
        showSnackBar('Workpack removed successfully', 'success');
      }
      
      // Update the local state to reflect the change
      updateValue('workpackNumber', value);
    } catch (error) {
      console.error('Error updating workpack:', error);
      showSnackBar('Failed to update workpack', 'error');
    }
  }, [poi?.id, poi?.workpack?.id, workpackIdMap, assignWorkpack, removeWorkpack, showSnackBar, updateValue]);
  const isEditing = editPointOfInterest?.formState === 'Update-General';
  const unitConversionSystem = useAtomValue(state.unitSystem);
  const selectedPoiHeight = useMemo(() => {
    const height = poi?.deckRelativeHeight;
    return localiseMeasurement({
      unitSystem: unitConversionSystem,
      sourceUnit: UnitsEnum.Meter,
      value: height,
    });
  }, [poi, unitConversionSystem]);
  const { checkByType } = useIsPoiWorkScope();

  const selectedPoiViewpointRelativeHeight = useMemo(() => {
    if (poi?.createdAt && !poi.viewpointRelativeHeight) return undefined;
    const height = poi?.viewpointRelativeHeight ?? heightToPointOfInterest;
    return localiseMeasurement({
      unitSystem: unitConversionSystem,
      sourceUnit: UnitsEnum.Meter,
      value: height,
    });
  }, [poi?.createdAt, poi?.viewpointRelativeHeight, heightToPointOfInterest, unitConversionSystem]);

  const isWorkScope = checkByType(poi?.type || '');
  return (
    <>
      {/* Only show POI ID while editing because POI ID is not available (or editable)
      at the time of POI creation. Its generated on the server side.
      */}
      {isEditing && (
        <CustomTextField
          mode="Edit"
          id="id"
          title={'POI ID'}
          value={poiId}
          sx={{ mt: 2 }}
          disabled
        />
      )}

      <CustomTextField
        mode="Edit"
        id="Name"
        title={`${currentPoiType} name`}
        onFieldValueChange={(value) => updateValue('name', value)}
        value={name}
        sx={{ mt: 2 }}
      />
      <DropDownField
        id="paint-region-general"
        data-testid="paint-region-general-selector"
        placeholder="Select Paint Block"
        key={selectedPaintRegionId}
        value={paintRegionOptions?.find(opt => opt.value === selectedPaintRegionId)?.label || ''}
        mode="Edit"
        title="Paint Block"
        options={paintRegionOptions?.map(opt => opt.label) || []}
        onFieldValueChange={handlePaintRegionChange}
      />
      <DropDownField
        mode="Edit"
        id="Visibility"
        title="Visibility"
        options={Object.values(VisibilityEnum)}
        value={visibility}
        sx={{ mt: 2 }}
        onFieldValueChange={(value) => updateValue('visibility', value as VisibilityEnum)}
        required
      />
      {visibility === VisibilityEnum.Custom && (
        <AbyssAutoComplete
          variant="standard"
          label="Select Teams"
          loading={teamsLoading}
          options={teamOptions || []}
          selectedValues={teamIds || []}
          handleSelected={(event) => updateValue('teamIds', event)}
          sx={{ mt: 2 }}
          required
        />
      )}
      <CustomDateField
        format="dd MMM yyyy"
        mode="Edit"
        id="Due Date"
        title="Due Date"
        value={dueDate}
        sx={{ mt: 2, mb: 0 }}
        onFieldValueChange={(value) => updateValue('dueDate', value)}
      />

      <DropDownField
        mode="Edit"
        id="Status"
        title="Status"
        options={Object.values(PoiStatus)}
        value={status}
        sx={{ mt: 2 }}
        onFieldValueChange={(value) => updateValue('status', value)}
      />

      <CustomTextField
        mode="Edit"
        id="Assign to"
        title="Assignee"
        value={assignee}
        sx={{ mt: 2 }}
        onFieldValueChange={(value) => updateValue('assignee', value)}
      />

      <Box sx={{ mt: 2 }}>
        <DropDownField
          mode={poi?.workpack ? 'ReadOnly' : 'Edit'}
          id="Workpack"
          title="Workpack"
          options={['', ...(workpackOptions || [])]}
          value={poi?.workpack?.name || ''}
          onFieldValueChange={handleWorkpackChange}
          disabled={!!poi?.workpack}
          placeholder="Select a workpack"
        />
        {poi?.workpack && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 1.5 }}>
            This field is managed by Work Pack assignment
          </Typography>
        )}
        {!poi?.workpack && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 1.5 }}>
            Select a workpack to assign
          </Typography>
        )}
      </Box>

      <CustomTextField
        mode="Edit"
        id="Comment"
        title="Comment"
        value={comment}
        sx={{ mt: 2 }}
        onFieldValueChange={(value) => updateValue('comment', value)}
      />

      {!isWorkScope && (
        <>
          <CustomTextField
            mode="ReadOnly"
            id="deckRelativeHeight"
            title="POI Height from Deck"
            value={selectedPoiHeight ?? 'N/A'}
            sx={{ mt: 4 }}
          />
          <CustomTextField
            mode="ReadOnly"
            id="viewpointDistance"
            title="POI Height from Viewpoint"
            value={selectedPoiViewpointRelativeHeight ?? 'N/A'}
            sx={{ mt: 2 }}
          />
        </>
      )}

      <DropDownField
        mode="Edit"
        id="Accessibility"
        title="Accessibility"
        options={Object.values(PoiAccessibilityEnum).map(
          (item) => formatEnumForDisplay(item) || ''
        )}
        value={formatEnumForDisplay(accessibility)}
        sx={{ mt: 2 }}
        onFieldValueChange={(value) => updateValue('accessibility', value)}
      />
    </>
  );
};
